<script>
export default {
  name: 'BarrasRegion',
  props: {
    labels: {
      type: Array,
      default: () => []
    },
    data: {
      type: Array,
      default: () => []
    },
    detalle: {
      type: Array,
      default: () => []
    },
    vista: {
      type: String,
      default: 'ventas'
    }
  },
  data() {
    return {
      chartInstance: null
    };
  },
  computed: {
    chartData() {
      if (!this.detalle || this.detalle.length === 0) {
        return {
          labels: [],
          datasets: []
        };
      }

      // Extraer regiones únicas y ordenarlas por total de ventas
      const regionesConTotales = this.labels.map((region, index) => ({
        region: region,
        totalVentas: this.data[index] || 0
      })).sort((a, b) => b.totalVentas - a.totalVentas);

      const regionesOrdenadas = regionesConTotales.map(r => r.region);

      // Obtener todas las empresas únicas
      const empresasUnicas = new Set();
      this.detalle.forEach(regionItem => {
        if (regionItem.empresas) {
          regionItem.empresas.forEach(empresa => {
            empresasUnicas.add(empresa.nombreEmpresa);
          });
        }
      });

      const listaEmpresas = Array.from(empresasUnicas);

      // Crear datasets por empresa
      const datasets = listaEmpresas.map(nombreEmpresa => {
        const data = regionesOrdenadas.map(region => {
          const regionItem = this.detalle.find(d => d.region === region);
          if (regionItem && regionItem.empresas) {
            const empresaData = regionItem.empresas.find(e => e.nombreEmpresa === nombreEmpresa);
            if (empresaData) {
              return this.vista === 'ventas' ? empresaData.totalVentas : empresaData.totalUnidades;
            }
          }
          return 0;
        });

        return {
          label: nombreEmpresa,
          data: data,
          backgroundColor: this.getColorForEmpresa(nombreEmpresa),
          borderColor: this.getColorForEmpresa(nombreEmpresa),
          borderWidth: 1
        };
      });

      // Guardar datos completos para tooltips
      this.datosCompletos = {
        regiones: regionesOrdenadas,
        detalle: this.detalle
      };

      return {
        labels: regionesOrdenadas,
        datasets: datasets
      };
    },

    chartOptions() {
      const self = this;
      return {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          xAxes: [{
            ticks: {
              beginAtZero: true,
              callback: function(value) {
                const numValue = parseFloat(value);
                
                if (self.vista === 'ventas') {
                  if (numValue >= 1000000000) {
                    return '$' + (numValue / 1000000000).toFixed(1) + 'B';
                  } else if (numValue >= 1000000) {
                    return '$' + (numValue / 1000000).toFixed(0) + 'M';
                  } else if (numValue >= 1000) {
                    return '$' + (numValue / 1000).toFixed(0) + 'K';
                  } else {
                    return '$' + Math.round(numValue);
                  }
                } else {
                  return self.formatNumber(numValue);
                }
              }
            },
            gridLines: {
              display: true,
              color: 'rgba(0,0,0,0.1)'
            }
          }],
          yAxes: [{
            gridLines: {
              display: false
            },
            ticks: {
              fontSize: 11
            }
          }]
        },
        legend: {
          display: true,
          position: 'top',
          labels: {
            usePointStyle: true,
            padding: 15
          }
        },
        tooltips: {
          backgroundColor: 'rgba(0,0,0,0.9)',
          titleFontColor: 'white',
          bodyFontColor: 'white',
          borderColor: 'rgba(255,255,255,0.3)',
          borderWidth: 1,
          cornerRadius: 6,
          mode: 'index',
          intersect: false,
          callbacks: {
            title: function(tooltipItems, data) {
              return 'Región ' + tooltipItems[0].label;
            },
            label: function(tooltipItem, data) {
              const empresa = data.datasets[tooltipItem.datasetIndex].label;
              const region = tooltipItem.label;
              
              if (self.datosCompletos && self.datosCompletos.detalle) {
                const regionItem = self.datosCompletos.detalle.find(d => d.region === region);
                if (regionItem && regionItem.empresas) {
                  const empresaData = regionItem.empresas.find(e => e.nombreEmpresa === empresa);
                  if (empresaData) {
                    if (self.vista === 'ventas') {
                      return [
                        `${empresa}: ${self.formatPrice(empresaData.totalVentas)}`,
                        `Unidades: ${self.formatNumber(empresaData.totalUnidades)}`,
                        `OCs: ${empresaData.cantidadOCs}`
                      ];
                    } else {
                      return [
                        `${empresa}: ${self.formatNumber(empresaData.totalUnidades)} unidades`,
                        `Ventas: ${self.formatPrice(empresaData.totalVentas)}`,
                        `OCs: ${empresaData.cantidadOCs}`
                      ];
                    }
                  }
                }
              }
              
              const valor = tooltipItem.value;
              return `${empresa}: ${self.vista === 'ventas' ? self.formatPrice(valor) : self.formatNumber(valor)}`;
            }
          }
        }
      };
    }
  },

  methods: {
    getColorForEmpresa(empresa) {
      const colores = {
        'GASCO GLP S A': '#0D47A1',
        'GASCO GLP S.A.': '#0D47A1', 
        'ABASTIBLE S.A.': '#FF9800',
        'EMPRESAS LIPIGAS S A': '#ffc107',
        'EMPRESAS LIPIGAS S.A.': '#ffc107'
      };
      return colores[empresa] || '#9E9E9E';
    },

    formatPrice(value) {
      if (!value) return '$0';
      const numValue = parseFloat(value);
      
      if (numValue >= 1000000000) {
        return '$' + (numValue / 1000000000).toFixed(1) + 'B';
      } else if (numValue >= 1000000) {
        return '$' + (numValue / 1000000).toFixed(0) + 'M';
      } else if (numValue >= 1000) {
        return '$' + (numValue / 1000).toFixed(0) + 'K';
      } else {
        return '$' + Math.round(numValue).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");
      }
    },

    formatNumber(value) {
      if (!value) return '0';
      const numValue = Math.round(parseFloat(value));
      return numValue.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");
    },

    renderChart() {
      if (this.chartInstance) {
        this.chartInstance.destroy();
      }

      const ctx = this.$refs.canvas.getContext('2d');
      const Chart = window.Chart;

      if (!Chart) {
        console.error('Chart.js no está disponible');
        return;
      }

      this.chartInstance = new Chart(ctx, {
        type: 'horizontalBar',
        data: this.chartData,
        options: this.chartOptions
      });
    }
  },

  mounted() {
    this.$nextTick(() => {
      this.renderChart();
    });
  },

  watch: {
    chartData: {
      handler() {
        this.$nextTick(() => {
          this.renderChart();
        });
      },
      deep: true
    },
    vista() {
      this.$nextTick(() => {
        this.renderChart();
      });
    }
  },

  beforeDestroy() {
    if (this.chartInstance) {
      this.chartInstance.destroy();
    }
  }
};
</script>

<template>
  <div style="position: relative; height: 100%;">
    <div v-if="!detalle || detalle.length === 0" class="text-xs-center pa-4">
      <v-icon size="48" color="grey lighten-2">place</v-icon>
      <div class="subheading grey--text mt-2">Sin datos de regiones</div>
    </div>
    <canvas v-else ref="canvas"></canvas>
  </div>
</template>